import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Check, ShieldCheck } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useConfirmVaGov } from '../../../services/onboardingBoard';

// "Do you have a VA.gov account?" on the rep's side (restructure 2026-09-24).
// Shows what the veteran said at intake and lets the rep confirm it on the
// call. Never a stop for signing up: a No just means setting it up is the
// veteran's first onboarding step (the client portal walks them through it).

export const VA_GOV_LABEL = { yes: 'Yes', no: 'No', unsure: "I don't know" };

const OPTIONS = ['yes', 'no', 'unsure'];

// The answer that counts: the rep's confirmation, else the intake answer.
export const vaGovAnswerOf = (lead) =>
  lead?.leadVaGovConfirmed || lead?.leadVaGovAccount || null;

const selectedTone = (v) =>
  v === 'yes'
    ? 'bg-emerald-600 text-white shadow-sm'
    : 'bg-amber-500 text-white shadow-sm';

// inline: just the three answers, for a row in Confirm the facts (Nic
// 2026-10-12: VA.gov is one of the facts, required before Confirm facts).
// onChange(answer) reports the pick to the host.
const VaGovCheck = ({ lead, className, inline = false, onChange = null }) => {
  const [confirmed, setConfirmed] = useState(lead?.leadVaGovConfirmed || null);
  // Saves in flight (Nic 2026-09-25: a quick No -> I don't know flashed
  // back). While the rep's own save is pending, a board refresh that still
  // carries the older answer must not overwrite the pick. Rapid clicks
  // queue: one save at a time, and the last pick is what lands.
  const saving = useRef(false);
  const queued = useRef(null);
  const confirm = useConfirmVaGov();

  // A board refresh (another tab, a teammate) wins, but only when nothing
  // of ours is still on its way.
  useEffect(() => {
    if (saving.current || queued.current) return;
    setConfirmed(lead?.leadVaGovConfirmed || null);
  }, [lead?.leadId, lead?.leadVaGovConfirmed]);

  if (!lead?.leadId) return null;
  const intake = lead.leadVaGovAccount || null;
  const needsSetup = (confirmed || intake) !== 'yes';

  const save = async (answer) => {
    saving.current = true;
    try {
      await confirm.mutateAsync({ leadId: lead.leadId, answer });
    } catch (err) {
      queued.current = null;
      setConfirmed(lead?.leadVaGovConfirmed || null);
      toast.error(err?.response?.data?.msg || "That couldn't be saved.");
    } finally {
      saving.current = false;
    }
    const next = queued.current;
    queued.current = null;
    if (next && next !== answer) save(next);
  };

  const pick = (v) => {
    if (v === confirmed) return;
    setConfirmed(v);
    if (onChange) onChange(v);
    if (saving.current) {
      queued.current = v;
      return;
    }
    save(v);
  };

  if (inline) {
    return (
      <div
        role="radiogroup"
        aria-label="Do they have a VA.gov account?"
        className={cn('inline-flex h-8 gap-0.5 rounded-lg bg-white p-0.5 shadow-sm ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10', className)}
      >
        {OPTIONS.map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={confirmed === v}
            onClick={() => pick(v)}
            className={cn(
              'flex items-center justify-center whitespace-nowrap rounded-md px-2.5 text-xs font-semibold outline-none focus:outline-none',
              confirmed === v
                ? selectedTone(v)
                : 'text-slate-500 hover:bg-slate-50 dark:text-slate-300 dark:hover:!bg-white/10',
            )}
          >
            {VA_GOV_LABEL[v]}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
          <ShieldCheck size={13} />
          VA.gov account
        </div>
        {confirmed ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            <Check size={12} strokeWidth={3} />
            Confirmed
          </span>
        ) : (
          <span className="whitespace-nowrap text-[11px] font-semibold text-amber-600 dark:text-amber-400">
            Ask on the call
          </span>
        )}
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400">
        {intake ? (
          <>
            They said{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {VA_GOV_LABEL[intake]}
            </span>{' '}
            at intake
          </>
        ) : (
          'Not asked at intake'
        )}
      </div>

      <div
        role="radiogroup"
        aria-label="Confirm their VA.gov account"
        // flex (not a 3-col grid): "I don't know" is wider than a third of
        // the narrow person panel, so each option grows evenly but never
        // below its own label, and every label stays centered.
        className="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/[0.06]"
      >
        {OPTIONS.map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={confirmed === v}
            onClick={() => pick(v)}
            className={cn(
              'flex h-8 min-w-max flex-1 items-center justify-center whitespace-nowrap rounded-lg px-2.5 text-xs font-semibold',
              confirmed === v
                ? selectedTone(v)
                : 'text-slate-600 hover:bg-white hover:text-slate-900 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white',
            )}
          >
            {VA_GOV_LABEL[v]}
          </button>
        ))}
      </div>

      {/* What each answer means for onboarding (Nic 2026-10-05). */}
      {confirmed === 'yes' ? (
        <p className="mb-0 text-xs text-slate-500 dark:text-slate-400">
          They land in onboarding ready to work. Their specialist sees them
          sign in on the first call.
        </p>
      ) : needsSetup || confirmed ? (
        <p className="mb-0 text-xs text-slate-500 dark:text-slate-400">
          Not a stop. Sign them up; setting up VA.gov is their first step,
          and their portal walks them through it.
        </p>
      ) : null}
    </div>
  );
};

export default VaGovCheck;
